import { useEffect, useState } from 'react'
import { Modal } from 'medere-ui-framework'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { ProgressSpinner } from 'primereact/progressspinner'
import {
  ContractHonorariumHistoryItemDTO,
  ContractHonorariumListItemDTO,
} from '../../../dto/contractHonorarium/ContractHonorariumDTO'
import { getContractHonorariumHistory } from '../../../services/ContractHonorariumController'
import { parseApiError } from '../../../services/apiError'
import { describeValues, formatIsoDate, formatIsoDateTime, nameOrGeneral } from '../contractHonorariumFormat'
import { describeRow } from './describeRow'

interface ContractHonorariumHistoryModalProps {
  contractHonorarium: ContractHonorariumListItemDTO | null
  onClose: () => void
  onError: (message: string) => void
}

export default function ContractHonorariumHistoryModal({
  contractHonorarium,
  onClose,
  onError,
}: Readonly<ContractHonorariumHistoryModalProps>) {
  const [entries, setEntries] = useState<ContractHonorariumHistoryItemDTO[]>([])
  const [loading, setLoading] = useState(false)
  const id = contractHonorarium?.contracthonorariumid ?? null

  useEffect(() => {
    if (id === null) return
    let cancelled = false
    setEntries([])
    setLoading(true)
    getContractHonorariumHistory(id)
      .then((loaded) => {
        if (!cancelled) setEntries(loaded)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const apiError = parseApiError(error)
        onError(apiError.errorCode === 'CONTRACT_HONORARIUM_NOT_FOUND'
          ? apiError.message
          : 'No se pudo cargar el historial. Intentá de nuevo.')
        onClose()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <Modal
      visible={contractHonorarium != null}
      setVisible={(nextVisible) => {
        if (!nextVisible) onClose()
      }}
      header={<span className='chd-modal-title'>Historial de cambios</span>}
      width='w-xlarge'
      className='chd-modal'
      footerButtonLeftText='Cerrar'
      onClickLeftBtn={onClose}
    >
      <div className='chd-modal-body'>
        {contractHonorarium && <p className='chd-confirm-text'>{describeRow(contractHonorarium)}</p>}
        {loading ? (
          <div className='md-loading' role='status'>
            <ProgressSpinner strokeWidth='4' />
            <span>Cargando…</span>
          </div>
        ) : (
          <DataTable
            value={entries}
            dataKey='id'
            size='small'
            scrollable
            scrollHeight='20rem'
            className='chd-modal-table'
            emptyMessage='Todavía no hay cambios registrados.'
          >
            <Column
              header='Desde'
              align='center'
              body={(entry: ContractHonorariumHistoryItemDTO) => formatIsoDate(entry.validFrom)}
            />
            <Column
              header='Hasta'
              align='center'
              body={(entry: ContractHonorariumHistoryItemDTO) => (entry.validUntil ? formatIsoDate(entry.validUntil) : '—')}
            />
            <Column
              header='Emisor'
              body={(entry: ContractHonorariumHistoryItemDTO) => nameOrGeneral(entry.billingEntityName)}
            />
            <Column field='distributionTypeName' header='Tipo' />
            <Column header='Valores' body={(entry: ContractHonorariumHistoryItemDTO) => describeValues(entry)} />
            <Column
              header='Modificado el'
              align='center'
              body={(entry: ContractHonorariumHistoryItemDTO) => formatIsoDateTime(entry.changeDate)}
            />
            <Column field='userName' header='Usuario' />
          </DataTable>
        )}
      </div>
    </Modal>
  )
}
